import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Bell, Send, Users, Clock, CheckCircle, XCircle, 
  Loader2, RefreshCw, Megaphone, Calendar
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const NotificationsTab = () => {
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [notificationLog, setNotificationLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Custom notification form
  const [customNotification, setCustomNotification] = useState({
    title: "",
    body: "",
    url: "/"
  });

  const fetchData = async () => {
    try {
      const [countRes, logRes] = await Promise.all([
        axios.get(`${API}/notifications/subscribers/count`, { headers: getAuthHeader() }),
        axios.get(`${API}/notifications/log?limit=20`, { headers: getAuthHeader() })
      ]);
      setSubscriberCount(countRes.data.count);
      setNotificationLog(logRes.data.logs || []);
    } catch (error) {
      console.error("Error fetching notification data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSendTest = async () => {
    setSending(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/notifications/test`, {}, { headers: getAuthHeader() });
      setTestResult({ success: true, message: `Sent to ${res.data.sent} subscribers` });
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || "Failed to send" });
    } finally {
      setSending(false);
    }
  };

  const handleSendCustom = async (e) => {
    e.preventDefault();
    if (!customNotification.title || !customNotification.body) {
      alert("Please fill in title and body");
      return;
    }
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/notifications/send`, customNotification, { headers: getAuthHeader() });
      alert(`Notification queued for ${res.data.queued} subscribers`);
      setCustomNotification({ title: "", body: "", url: "/" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
            Push Notifications
          </h2>
          <p className="text-zinc-500 text-sm">Manage push notification subscribers and send alerts</p>
        </div>
        <Button 
          onClick={fetchData} 
          className="flex items-center gap-2 bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-6 bg-zinc-900/50 border border-white/5">
          <Users className="w-5 h-5 text-orange-500 mb-4" />
          <div className="font-secondary text-4xl font-black text-white">{subscriberCount}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest">Subscribers</div>
        </div>
        
        <div className="p-6 bg-zinc-900/50 border border-white/5">
          <Bell className="w-5 h-5 text-orange-500 mb-4" />
          <div className="font-secondary text-4xl font-black text-white">{notificationLog.length}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest">Recent Notifications</div>
        </div>
        
        <div className="p-6 bg-zinc-900/50 border border-white/5">
          <Clock className="w-5 h-5 text-orange-500 mb-4" />
          <div className="font-secondary text-lg font-black text-white">Every 1 min</div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest">Reminder Check Interval</div>
        </div>
      </div>

      {/* Test Notification */}
      <div className="p-4 bg-zinc-900/50 border border-white/5">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-orange-500" />
          Test Notification
        </h3>
        <p className="text-zinc-500 text-sm mb-4">
          Send a test notification to verify push is working for all subscribers.
        </p>
        <div className="flex items-center gap-4">
          <Button
            onClick={handleSendTest}
            disabled={sending || subscriberCount === 0}
            className="flex items-center gap-2 bg-orange-500 text-black hover:bg-orange-600 text-xs px-4 py-2 h-auto rounded-none"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Test
          </Button>
          {testResult && (
            <span className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-green-500' : 'text-red-500'}`}>
              {testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {testResult.message}
            </span>
          )}
        </div>
      </div>

      {/* Custom Notification */}
      <div className="p-4 bg-zinc-900/50 border border-white/5">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2">
          <Send className="w-5 h-5 text-orange-500" />
          Send Custom Notification
        </h3>
        <form onSubmit={handleSendCustom} className="space-y-4">
          <div>
            <label className="block text-xs text-zinc-500 uppercase mb-2">Title</label>
            <input
              type="text"
              value={customNotification.title}
              onChange={(e) => setCustomNotification(prev => ({ ...prev, title: e.target.value }))}
              placeholder="🏀 Breaking News"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase mb-2">Message Body</label>
            <textarea
              value={customNotification.body}
              onChange={(e) => setCustomNotification(prev => ({ ...prev, body: e.target.value }))}
              placeholder="Big game tonight! Don't miss it."
              rows={3}
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase mb-2">Click URL (optional)</label>
            <input
              type="text"
              value={customNotification.url}
              onChange={(e) => setCustomNotification(prev => ({ ...prev, url: e.target.value }))}
              placeholder="/schedule"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
          </div>
          <Button
            type="submit"
            disabled={sending || subscriberCount === 0}
            className="flex items-center gap-2 bg-white text-black hover:bg-zinc-200 text-xs px-6 py-2 h-auto rounded-none"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send to {subscriberCount} Subscribers
          </Button>
        </form>
      </div>

      {/* Automatic Reminders Info */}
      <div className="p-4 bg-orange-500/10 border border-orange-500/20">
        <h3 className="text-orange-500 font-bold mb-2 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Automatic Game Reminders
        </h3>
        <p className="text-zinc-400 text-sm">
          The system automatically checks for upcoming broadcasts every minute. When a game is 
          15-20 minutes away, subscribers receive a push notification with a "Watch Now" button.
        </p>
        <ul className="mt-3 text-xs text-zinc-500 space-y-1">
          <li>✓ Notifications include sport emoji and team names</li>
          <li>✓ Deep link directly to the live channel</li>
          <li>✓ Duplicate reminders are prevented via notification log</li>
        </ul>
      </div>

      {/* Notification Log */}
      {notificationLog.length > 0 && (
        <div className="p-4 bg-zinc-900/50 border border-white/5">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            Recent Notification Log
          </h3>
          <div className="space-y-2">
            {notificationLog.map((log, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0">
                <div>
                  <span className="text-white text-sm">{log.type || "notification"}</span>
                  {log.broadcast_id && (
                    <span className="text-zinc-500 text-xs ml-2">• Broadcast: {log.broadcast_id.slice(0, 8)}...</span>
                  )}
                </div>
                <span className="text-zinc-500 text-xs">
                  {new Date(log.sent_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default NotificationsTab;
